import { useState } from 'react';
import { PortalUser, clearLastVisitedPage } from '@frontend/auth-helpers';
import { DeviceSection } from '@frontend/calls-v2';
import { useTranslation } from '@frontend/i18n';
import { useHasPhoneSystemAccess } from '@frontend/phone-config';
import { isMobileDevice } from '@frontend/responsiveness';
import { useAppScopeStore } from '@frontend/scope';
import { shell } from '@frontend/shell-utils';
import { TourGuideHelpers } from '@frontend/tour-guide';
import { WeaveRewind } from '@frontend/weave-rewind';
import { theme } from '@frontend/theme';
import { Avatar, Heading, Text, TextLink } from '@frontend/design-system';
import { signOut } from '../../../../helpers/sign-out';
import { DownloadBanner } from '../../../grid-dashboard';
import { Section } from '../../layout/section';
import { AccountManagerSection } from './sections/account-manager-section';
import { HealthStatusSection } from './sections/health-status-section';
import { LinkSection } from './sections/link-section';
import { VersionSection } from './sections/version-section';

export const Profile = ({
  user,
  closeModal,
  nextVersion,
  handleOpenProfilePanel,
}: {
  user: PortalUser | undefined;
  nextVersion: string | undefined;
  closeModal: () => void;
  handleOpenProfilePanel: () => void;
}) => {
  const { selectedLocationIds } = useAppScopeStore();
  const hasPhoneSystemAccess = useHasPhoneSystemAccess({ locationIds: selectedLocationIds });
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  return (
    <div style={{ height: '100%', display: 'grid', gridTemplateRows: 'min-content 1fr min-content' }}>
      <Section css={{ display: 'flex', gap: theme.spacing(2), flexDirection: 'column' }}>
        <div css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div
            css={{
              display: 'grid',
              alignSelf: 'start',
              gridTemplateAreas: '"avatar name" "avatar username"',
              gridTemplateColumns: 'auto 1fr',
              columnGap: theme.spacing(2),
            }}
          >
            <Avatar
              isUser
              css={{ gridArea: 'avatar' }}
              disableClick
              name={user?.firstName + ' ' + user?.lastName}
              size='xl'
            />
            <Heading level={2} css={{ gridArea: 'name' }}>
              {user?.firstName + ' ' + user?.lastName}
            </Heading>
            <Text color='light' css={{ gridArea: 'username' }}>
              {user?.username}
            </Text>
          </div>
          <TextLink
            size='medium'
            weight='bold'
            onClick={async () => {
              try {
                setIsLoading(true);
                closeModal();
                TourGuideHelpers.tourGuideLocalStorageHelper.clearInfo();
                clearLastVisitedPage();
                await signOut();
              } catch (e) {
                setIsLoading(false);
              }
            }}
          >
            {t('Sign Out')}
          </TextLink>
        </div>
        {nextVersion && <VersionSection nextVersion={nextVersion} />}
      </Section>
      <Section css={{ padding: 0, display: 'flex', flexDirection: 'column', '> :last-child': { flexGrow: 1 } }}>
        <Section>{hasPhoneSystemAccess && <DeviceSection closeModal={closeModal} />}</Section>
        <WeaveRewind type='profile' />
        <HealthStatusSection />
      </Section>
      <Section
        render={(setShouldHide) => (
          <AccountManagerSection
            handleOpenProfilePanel={handleOpenProfilePanel}
            hideSection={() => setShouldHide(true)}
          />
        )}
      />
      {!shell.isShell && !isMobileDevice && (
        <Section>
          <DownloadBanner
            actionLabel={t('Download Desktop App', { ns: 'dashboard' })}
            trackingId='nwx-profile-download-weave-app-banner'
            type='download-permanent'
          >
            {t('Get faster performance, more features, native notifications, and enhanced personalization!', {
              ns: 'dashboard',
            })}
          </DownloadBanner>
        </Section>
      )}
      <LinkSection closeModal={closeModal} isLoading={isLoading} />
    </div>
  );
};
